// CoverageAnalysisPage — /collections/geolink/coverage
//
// Heat-map / cluster / dot visualisation of activity by outcode across
// bookings + skip_jobs over a trailing window. Auto-runs on mount with
// the default controls (last 90 days, density 1+, both sources on).

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Checkbox } from '../../../components/form/Checkbox';
import {
  CoverageMapView,
  type CoverageViewMode,
} from '../../../components/ui/CoverageMapView/CoverageMapView';
import { getCoverageData } from '../../../api/geolink';
import { queryKeys } from '../../../api/queryKeys';
import { cn } from '../../../utils/cn';
import styles from './CoverageAnalysisPage.module.css';

const PERIOD_OPTIONS: ReadonlyArray<{ value: 30 | 60 | 90 | 180 | 365; label: string }> = [
  { value: 30, label: '30d' },
  { value: 60, label: '60d' },
  { value: 90, label: '90d' },
  { value: 180, label: '180d' },
  { value: 365, label: '365d' },
];

const DENSITY_OPTIONS: ReadonlyArray<{ value: 1 | 6 | 16; label: string }> = [
  { value: 1, label: '1+' },
  { value: 6, label: '6+' },
  { value: 16, label: '16+' },
];

const VIEW_OPTIONS: ReadonlyArray<{ value: CoverageViewMode; label: string }> = [
  { value: 'heat', label: 'Heat' },
  { value: 'clusters', label: 'Clusters' },
  { value: 'dots', label: 'Dots' },
];

function PillSelector<T extends string | number>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return (
    <div className={styles.pillRow} role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => (
        <button
          key={String(opt.value)}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          className={cn(styles.pill, value === opt.value && styles.pillActive)}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function CoverageAnalysisPage() {
  const [period, setPeriod] = useState<30 | 60 | 90 | 180 | 365>(90);
  const [density, setDensity] = useState<1 | 6 | 16>(1);
  const [viewMode, setViewMode] = useState<CoverageViewMode>('heat');
  const [includeBookings, setIncludeBookings] = useState<boolean>(true);
  const [includeSkipJobs, setIncludeSkipJobs] = useState<boolean>(true);

  const coverageQuery = useQuery({
    queryKey: queryKeys.geolink.coverage({
      period_days: period,
      density,
      include_bookings: includeBookings,
      include_skip_jobs: includeSkipJobs,
    }),
    queryFn: () =>
      getCoverageData({
        period_days: period,
        density,
        include_bookings: includeBookings,
        include_skip_jobs: includeSkipJobs,
      }),
    staleTime: 60_000,
  });

  const data = coverageQuery.data;
  const points = data?.coverage_points ?? [];
  const topAreas = data?.top_areas ?? [];
  const opportunities = data?.opportunities ?? [];

  const insights = useMemo(() => {
    if (points.length === 0) {
      return { totalJobs: 0, totalRevenue: 0, outcodes: 0, avgPerOutcode: 0 };
    }
    const totalJobs = points.reduce((s, p) => s + p.job_count, 0);
    const totalRevenue = points.reduce((s, p) => s + p.total_revenue, 0);
    return {
      totalJobs,
      totalRevenue,
      outcodes: points.length,
      avgPerOutcode: totalJobs / points.length,
    };
  }, [points]);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Coverage Analysis"
        subtitle="Where you're winning — and where you could grow"
      />

      <Card>
        <div className={styles.controls}>
          <div className={styles.controlGroup}>
            <span>Period</span>
            <PillSelector value={period} options={PERIOD_OPTIONS} onChange={setPeriod} ariaLabel="Period" />
          </div>
          <div className={styles.controlGroup}>
            <span>Density floor</span>
            <PillSelector value={density} options={DENSITY_OPTIONS} onChange={setDensity} ariaLabel="Density" />
          </div>
          <div className={styles.controlGroup}>
            <span>View</span>
            <PillSelector value={viewMode} options={VIEW_OPTIONS} onChange={setViewMode} ariaLabel="View mode" />
          </div>
          <div className={styles.toggleRow}>
            <Checkbox
              label="Bookings"
              checked={includeBookings}
              onChange={(e) => setIncludeBookings(e.target.checked)}
            />
            <Checkbox
              label="Skip Jobs"
              checked={includeSkipJobs}
              onChange={(e) => setIncludeSkipJobs(e.target.checked)}
            />
          </div>
        </div>
      </Card>

      <div className={styles.layout}>
        <div className={styles.mapPanel}>
          <Card flush>
            <div style={{ height: 'calc(65vh - 1px)', minHeight: 540 }}>
              <CoverageMapView
                points={points}
                viewMode={viewMode}
                loading={coverageQuery.isLoading}
              />
            </div>
          </Card>
        </div>

        <aside className={styles.sideRail}>
          <Card title="Top Service Areas">
            {topAreas.length === 0 ? (
              <div className={styles.empty}>No areas to rank yet.</div>
            ) : (
              <ul className={styles.rankList}>
                {topAreas.map((a) => (
                  <li key={a.outcode} className={styles.rankRow}>
                    <span className={styles.outcode}>{a.outcode}</span>
                    <span>
                      <span className={styles.metric}>{a.job_count}</span>
                      <span className={styles.subMetric}> · £{a.total_revenue.toFixed(0)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Expansion Opportunities">
            {opportunities.length === 0 ? (
              <div className={styles.empty}>No low-coverage areas in this range.</div>
            ) : (
              <div className={styles.opportunityGrid}>
                {opportunities.map((o) => (
                  <div key={o.outcode} className={styles.opportunityCard}>
                    <div className={styles.outcode}>{o.outcode}</div>
                    <div className={styles.opportunityCount}>
                      {o.job_count} job{o.job_count === 1 ? '' : 's'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </aside>
      </div>

      <Card title="Quick Insights">
        <div className={styles.insightsRow}>
          <div className={styles.insightCard}>
            <span className={styles.insightLabel}>Total jobs</span>
            <span className={styles.insightValue}>{insights.totalJobs.toLocaleString()}</span>
          </div>
          <div className={styles.insightCard}>
            <span className={styles.insightLabel}>Total revenue</span>
            <span className={styles.insightValue}>£{insights.totalRevenue.toFixed(0)}</span>
          </div>
          <div className={styles.insightCard}>
            <span className={styles.insightLabel}>Outcodes covered</span>
            <span className={styles.insightValue}>{insights.outcodes}</span>
          </div>
          <div className={styles.insightCard}>
            <span className={styles.insightLabel}>Avg jobs / outcode</span>
            <span className={styles.insightValue}>{insights.avgPerOutcode.toFixed(1)}</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
